// libs
import type { Build } from '@todesktop/shared';
import { Text } from 'ink';
import { useEffect, useState, type ReactNode } from 'react';

// components
import BuildProgress from './BuildProgress';
import ErrorDisplay from './ErrorDisplay';
import MainLayout from './MainLayout';

// utils
import getLatestBuildId from '../utilities/getLatestBuildId';
import getProjectConfig from '../utilities/projectConfig/getProjectConfig';
import logForCI from '../utilities/logForCI';
import shouldExitOnBuildFailure from '../utilities/shouldExitOnBuildFailure';
import subscribeToBuild from '../utilities/subscribeToBuild';
import useExit from '../utilities/useExit';
import findAppUserId from '../utilities/findAppUserId';
import LoadingText from './LoadingText';
import validateBuildIdArg from '../utilities/validateBuildIdArg';

interface ViewBuildProps {
  commandUsed: string;
  id?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  shouldViewLatest?: boolean;
  configPath?: string;
}

export default function ViewBuild({
  commandUsed,
  id,
  configPath,
}: ViewBuildProps) {
  const exit = useExit();
  const [
    {
      appId,
      arbitraryMessageComponent,
      build,
      error,
      hasBuildEverFailed,
      isLoading,
    },
    setState,
  ] = useState<ViewBuildState>({
    appId: null,
    arbitraryMessageComponent: null,
    build: null,
    error: null,
    hasBuildEverFailed: false,
    isLoading: true,
  });

  const onError = (e: any) => {
    const errorData = e.response ? e.response.data : e;
    logForCI(errorData);
    setState((prevState) => ({
      ...prevState,
      error: errorData,
    }));
  };

  useEffect(() => {
    let firebaseUnsubscribe: (() => void) | undefined;

    async function viewBuild() {
      let config;
      try {
        // This will throw if validation fails:
        config = getProjectConfig(configPath).config;
      } catch (e) {
        setState((previousState) => ({ ...previousState, error: e }));
        return;
      }

      const { id: userId } = await findAppUserId(config.id);

      const subscribe = (buildId: string) => {
        subscribeToBuild({
          appId: config.id,
          buildId,
          onDataReceived: (data) => {
            if (!data) {
              onError(
                new Error(
                  `No such build exists (${buildId}). Please contact us if this is an error`,
                ),
              );
            }
            setState((prevState) => ({
              ...prevState,
              appId: config.id,
              build: data as Build,
              isLoading: false,
            }));
          },
          userId,
        })
          .then((firebaseUnsubscribeResult) => {
            firebaseUnsubscribe = firebaseUnsubscribeResult;
          })
          .catch(onError);
      };

      if (id) {
        validateBuildIdArg({ buildId: id, commandUsed });
        subscribe(id);
      } else {
        getLatestBuildId({ appId: config.id, userId })
          .catch(onError)
          .then((latestBuildId) => {
            if (!latestBuildId) {
              setState((previousState) => ({
                ...previousState,
                arbitraryMessageComponent: <Text>There are no builds yet</Text>,
              }));
              return;
            }
            return subscribe(latestBuildId);
          });
      }
    }

    viewBuild();

    return () => {
      if (firebaseUnsubscribe) {
        firebaseUnsubscribe();
      }
    };
  }, [id, commandUsed, configPath]);

  // Exit if the build has errored and settled
  useEffect(() => {
    if (hasBuildEverFailed && build && shouldExitOnBuildFailure(build)) {
      setTimeout(() => exit(new Error('Build has failed')), 10);
    }
  }, [build, exit, hasBuildEverFailed]);

  // Exit once an arbitrary message exists
  useEffect(() => {
    if (arbitraryMessageComponent) {
      setTimeout(() => exit(new Error('Validation failed')), 10);
    }
  }, [arbitraryMessageComponent, exit]);

  if (error) {
    return <ErrorDisplay commandUsed={commandUsed} error={error} />;
  }

  if (arbitraryMessageComponent) {
    return arbitraryMessageComponent;
  }

  if (isLoading || !build) {
    return <LoadingText />;
  }

  const onBuildFailure = () => {
    if (hasBuildEverFailed) {
      return;
    }
    setState((prevState) => ({
      ...prevState,
      hasBuildEverFailed: true,
    }));
  };

  return (
    <MainLayout
      appId={appId ?? undefined}
      appName={build.appName}
      appVersion={build.appVersion}
      build={build}
      commandUsed={commandUsed}
      hasBuildEverFailed={hasBuildEverFailed}
    >
      <BuildProgress build={build} onBuildFailure={onBuildFailure} />
    </MainLayout>
  );
}

interface ViewBuildState {
  appId: string | null;
  arbitraryMessageComponent: ReactNode | null;
  build: Build | null;
  error: unknown;
  hasBuildEverFailed: boolean;
  isLoading: boolean;
}
